"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { X, UserPlus } from "lucide-react";

type RegisterToViewVariant = "fullProfile" | "moreMatches";

interface RegisterToViewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: RegisterToViewVariant;
}

const fullProfileBenefits = [
  "ऑनलाईन चॅटिंग",
  "स्थळांना फ्री शॉर्टलिस्टसह इंटरेस्ट पाठवा",
  "फ्री कुंडली मॅचिंग करता येणार",
  "अनुरूप स्थळांचे मोबाईल क्रमांक मिळवा",
];

const variantContent = {
  fullProfile: {
    title: "View Full Profile",
    intro:
      "संपूर्ण प्रोफाईल पाहण्यासाठी फ्री नोंदणी करा. वेरिफाइड वधू-वरांशी संपर्क साधा.",
    benefitsHeading: "फ्री नोंदणीचे फायदे",
    benefits: fullProfileBenefits,
  },
  moreMatches: {
    title: "View More Matches",
    intro:
      "वधू-वराचे अधिक प्रोफाईल्स पाहण्यासाठी फ्री नोंदणी करा. नोंदणीनंतर हजारो वेरिफाइड प्रोफाईल्स पाहता येतील.",
    benefitsHeading: "फ्री नोंदणीचे फायदे",
    benefits: fullProfileBenefits,
  },
};

export default function RegisterToViewPopup({
  isOpen,
  onClose,
  variant = "fullProfile",
}: RegisterToViewPopupProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  const content = variantContent[variant];

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="relative bg-gradient-to-r from-primary-600 to-secondary-600 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 transition-colors hover:bg-white/30"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
              <UserPlus className="h-5 w-5" />
            </div>
            <h2 className="text-xl font-bold">{content.title}</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="mb-6 space-y-3 text-gray-600">
            <p className="leading-relaxed">{content.intro}</p>
            <h3 className="pt-1 font-semibold text-gray-800">
              {content.benefitsHeading}
            </h3>
            <ul className="list-inside list-disc space-y-1.5 pl-1 text-gray-700">
              {content.benefits.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-3">
            <Link
              href="https://marathivadhuvarsuchak.com/register"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-700"
            >
              <UserPlus className="h-5 w-5" />
              Register Free
            </Link>
            <button
              onClick={onClose}
              className="w-full rounded-xl px-6 py-2 font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
